import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import { CiSearch } from "react-icons/ci";
import { IoNotificationsOutline } from "react-icons/io5";
import { useStructuredNotifications } from "../api/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";

import Avatar from "../assets/images/avatar.png";
import Logo from "../assets/images/Logo.png";
import { useAuth } from "../hooks/auth_context";
import { signOutUser } from "../firebase_auth/firebase_auth";

// Admin notification bell — polls every 60s via REST. Empty by default until
// admin-targeted events are added (future prompt). The surface is ready.
const AdminBell: React.FC<{ uid?: string | null; token?: string }> = ({ uid, token }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data } = useStructuredNotifications(uid, token);
  const queryClient = useQueryClient();
  const notifications = data?.notifications ?? [];
  const unread = notifications.filter((n) => !n.read).length;

  const handleMarkAllRead = async () => {
    if (!uid) return;
    const hasUnread = notifications.some((n) => !n.read);
    if (!hasUnread) return;

    // Snapshot the previous cache so we can revert if the write fails.
    const previous = queryClient.getQueryData<any>([
      "notificationsStructured",
      uid,
    ]);

    // Optimistic update — flip the cached query data to read:true so the
    // bell badge clears + dropdown rows lose the dot the instant the
    // button is pressed. The backend write below reconciles via Admin SDK,
    // so the next refetch will see the same state and won't flash unread
    // items back.
    queryClient.setQueryData<any>(
      ["notificationsStructured", uid],
      (prev: any) => {
        if (!prev?.notifications) return prev;
        return {
          ...prev,
          notifications: prev.notifications.map((n: any) => ({
            ...n,
            read: true,
          })),
        };
      }
    );

    try {
      // Backend uses Admin SDK to update the same path the GET reads from,
      // so the post-write invalidate is guaranteed consistent. Previously
      // we batched writes through the client Firestore SDK, which could
      // race the REST refetch (or silently fail) and let unread items
      // flash back into the bell.
      await apiClient.post(
        `notifications-structured/${uid}/mark-all-read`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
      );
      queryClient.invalidateQueries({ queryKey: ["notificationsStructured", uid] });
    } catch (e) {
      console.error("markAllRead failed", e);
      // Revert the optimistic update so the badge doesn't lie.
      if (previous) {
        queryClient.setQueryData(
          ["notificationsStructured", uid],
          previous
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: ["notificationsStructured", uid],
        });
      }
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const relTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
    return `${Math.floor(diff / 86400_000)}d ago`;
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative text-[#666666] hover:text-[#16729A] transition-colors"
      >
        <IoNotificationsOutline size={22} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1.5 bg-[#B51D28] text-white text-[10px] font-bold min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-10 right-0 z-50 bg-white border border-[#E0E0E0] shadow-lg rounded-[12px] w-[320px] max-h-[380px] flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E0E0E0] flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <IoNotificationsOutline size={16} className="text-[#16729A]" />
              <span className="text-sm font-semibold text-[#1A1A1A]">Notifications</span>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-[11px] text-[#16729A] hover:underline font-medium"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#9CA3AF]">No notifications yet</div>
            ) : (
              <ul className="p-2 flex flex-col gap-0.5">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <div className={`flex gap-3 items-start p-2.5 rounded-lg ${!n.read ? "bg-[#F0F7FB]" : "hover:bg-[#F5F5F5]"}`}>
                      <div className="w-7 h-7 rounded-full bg-[#16729A]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <IoNotificationsOutline size={13} className="text-[#16729A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-[#1A1A1A] truncate">{n.title}</p>
                        {n.body && <p className="text-[12px] text-[#666666] line-clamp-1">{n.body}</p>}
                        <p className="text-[11px] text-[#9CA3AF] mt-0.5">{relTime(n.createdAt)}</p>
                      </div>
                      {!n.read && <span className="shrink-0 mt-2 w-2 h-2 rounded-full bg-[#16729A]" />}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOutUser();
    navigate("/login");
  };

  return (
    <nav className="sticky top-0 z-30 w-full px-3 pt-3 pb-2 bg-[#F5F5F5]/85 backdrop-blur supports-[backdrop-filter]:bg-[#F5F5F5]/70">
      <div className="mx-auto max-w-[1280px] bg-white border border-[#E0E0E0] rounded-[12px] px-6 py-3 shadow-[0_1px_4px_rgba(0,0,0,0.04)]">
        <div className="flex items-center justify-between gap-4">
          {/* Left: logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 no-underline" aria-label="TieUp Admin home">
            <img src={Logo} alt="TieUp" className="w-9 h-9 object-contain" />
            <span className="text-base font-bold text-[#1A1A1A]">TieUp</span>
          </Link>

          {/* Center: search */}
          <div className="hidden md:flex items-center gap-2 flex-1 max-w-md mx-4">
            <CiSearch size={18} className="text-[#9CA3AF] shrink-0" />
            <input
              type="text"
              placeholder="Type to search..."
              className="focus:outline-none w-full text-sm text-[#1A1A1A] placeholder-[#9CA3AF] bg-transparent"
            />
          </div>

          {/* Right: bell + avatar dropdown */}
          <div className="flex items-center gap-3 shrink-0">
            <AdminBell uid={currentUser?.userId ?? undefined} token={currentUser?.accessToken} />

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="flex items-center gap-3 cursor-pointer"
                onClick={() => setIsOpen((v) => !v)}
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-[#1A1A1A] leading-tight">
                    {currentUser.fullName || "Admin"}
                  </p>
                  <p className="text-xs text-[#666666] leading-tight">{currentUser.email}</p>
                </div>
                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#FFE8E8] text-[#B51D28]">
                  Admin
                </span>
                <img
                  className="w-9 h-9 rounded-full object-cover border border-[#E0E0E0]"
                  src={currentUser.photoURL ? currentUser.photoURL : Avatar}
                  alt="user"
                />
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E0E0E0] rounded-[12px] shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-[#E0E0E0]">
                    <span className="block text-sm font-medium text-[#1A1A1A]">{currentUser.fullName || "Admin"}</span>
                    <span className="block text-xs text-[#666666] truncate">{currentUser.email}</span>
                  </div>
                  <ul className="py-1">
                    <li>
                      <Link to="/profile" onClick={() => setIsOpen(false)} className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#F5F5F5] no-underline">
                        My Profile
                      </Link>
                    </li>
                    <li>
                      <Link to="/profile" onClick={() => setIsOpen(false)} className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#F5F5F5] no-underline">
                        Account Settings
                      </Link>
                    </li>
                    <li>
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="w-full text-left flex items-center justify-between px-4 py-2.5 text-sm text-[#B51D28] hover:bg-[#FFE8E8]"
                      >
                        <span>Log out</span>
                        <HiArrowUpRight size={14} />
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
